import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@core/components/ui';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'text/csv': ['.csv'],
  'application/zip': ['.zip'],
};

/**
 * Overlay-style drag & drop zone (Dropbox/Drive pattern)
 *
 * Invisible by default. Shows a full-area overlay when files are dragged over the container.
 * The parent's "Upload" button triggers the file picker via the imperative `open()` handle.
 */
export const DropZone = forwardRef(function DropZone({ onUpload, isUploading = false, disabled = false, children }, ref) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setIsDragOver(false);
      if (acceptedFiles.length > 0) {
        const folderPaths = acceptedFiles.map((file) => file.path || '');
        onUpload(acceptedFiles, folderPaths);
      }
    },
    [onUpload],
  );

  const onDragEnter = useCallback(() => setIsDragOver(true), []);
  const onDragLeave = useCallback(() => setIsDragOver(false), []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    accept: ACCEPTED_TYPES,
    multiple: true,
    disabled: disabled || isUploading,
    noClick: true,
    noKeyboard: true,
  });

  useImperativeHandle(ref, () => ({ open }), [open]);

  return (
    <div {...getRootProps()} className="relative">
      {/* react-dropzone keeps rendering a visually hidden file input even when
          disabled, and only nulls its handlers. It stays in the accessibility
          tree as a "Choose File" control that does nothing, which is exactly
          the dead affordance read-only users should never be offered. */}
      {!disabled && <input {...getInputProps()} />}

      {/* Upload progress indicator */}
      {isUploading && (
        <div className="flex items-center gap-3 py-3 px-4 mb-4 bg-[var(--color-accent-subtle)] border border-[var(--color-accent)] rounded-lg">
          <Spinner size="sm" />
          <div>
            <p className="text-sm text-[var(--color-accent)] font-medium">{t('dataroom.dropZone.processing')}</p>
            <p className="text-xs text-[var(--color-text-secondary)]">
              {t('dataroom.dropZone.processingHint')}
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      {children}

      {/* Drag overlay */}
      {isDragOver && !isUploading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--color-accent-subtle)]/80 border-2 border-dashed border-[var(--color-accent)] rounded-lg backdrop-blur-xs">
          <div className="flex flex-col items-center gap-2">
            <Icon icon="mdi:file-download-outline" className="size-12 text-[var(--color-accent)]" />
            <p className="text-sm font-medium text-[var(--color-accent)]">{t('dataroom.dropZone.dropFiles')}</p>
          </div>
        </div>
      )}
    </div>
  );
});
